"use client";

import { useMemo } from "react";

import { Item, Layout } from '@/dto/item';
import { generateItemUrl } from '@/utils/itemUrlUtils';
import MenuCategory from '@/components/menu/menuCategory';
import MenuItem from '@/components/menu/menuItem';
import { ProcessedItem } from '@/types/menus';
import { Category } from '@/dto/category';
import { Markers } from '@/dto/marker';

interface CategoryItemsProps {
  readonly category: Category;
  readonly merchantName?: string;
  readonly itemTemplate?: string;
  readonly itemsMap?: Record<string, Item> | null;
  readonly query?: string;
  readonly getItemHref?: (item: Item) => string;
  readonly baseRoute?: string;
  readonly queryParams?: string;
  readonly markers?: Markers;
  readonly layout?: Layout;
}

export const CategoryItems = ({
  category,
  merchantName,
  itemTemplate,
  itemsMap,
  query,
  getItemHref,
  baseRoute,
  queryParams,
  markers,
  layout = "default",
}: CategoryItemsProps) => {
  const validItems = useMemo<ProcessedItem[]>(() => {
    const normalizedQuery = query?.toLowerCase() ?? "";

    if (!category.items) {
      return [];
    }

    return category.items.reduce((acc: ProcessedItem[], itemId: Item["id"]) => {
      const item = itemsMap?.[itemId];

      if (!item?.name?.toLowerCase().includes(normalizedQuery)) {
        return acc;
      }

      let url = "";
      if (getItemHref) {
        url = getItemHref(item);
      } else if (baseRoute) {
        url = generateItemUrl({
          item,
          baseRoute,
          queryParams: queryParams ?? "",
        });
      }

      acc.push({ item, url });

      return acc;
    }, []);
  }, [
    category.items,
    category.id,
    itemsMap,
    query,
    getItemHref,
    baseRoute,
    queryParams,
  ]);

  if (validItems.length === 0) {
    return null;
  }

  return (
    <MenuCategory
      categoryName={category.name}
      categoryId={category.id}
      sortOrder={category.sortOrder}
      items={validItems}
      merchantName={merchantName}
      itemTemplate={itemTemplate}
      renderMenuItem={props => <MenuItem {...props} />}
      markers={markers}
      layout={layout}
    />
  );
};
